import { createSelector, createSlice } from '@reduxjs/toolkit'
import { initLoadable, Loadable } from 'state'
import { RootState } from 'state/store'
import { types } from '@concordia/super-sdk'
import { isEmptyOrNil } from 'toolbox/account'
import { fetchBalanceHistory } from 'state/thunks/fetchBalanceHistory'

type State = Loadable<types.SBalanceHistory>
export const initialState = initLoadable<types.SBalanceHistory>(null)

const balanceHistory = createSlice({
  name: 'balanceHistory',
  initialState,
  reducers: {
    resetBalanceHistory: (state: State) => {
      state.value = null
      state.loadedOnce = false
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBalanceHistory.fulfilled, (state, action) => {
      state.loadedOnce = true
      state.value = action.payload
      state.status = 'idle'
    })

    builder.addCase(fetchBalanceHistory.pending, (state) => {
      state.status = 'busy'
    })
  }
})

export const { resetBalanceHistory } = balanceHistory.actions
export default balanceHistory.reducer
export const selectBalanceHistory = (s: RootState) => s.user.balanceHistory.value
export const selectBalanceHistoryBusy = (s: RootState) => s.user.balanceHistory.status === 'busy'
export const selectBalanceHistoryLoaded = (s: RootState) => s.user.balanceHistory.loadedOnce

export const selectLoadedBalanceHistory = createSelector(
  [selectBalanceHistory, selectBalanceHistoryLoaded],
  (bals, loaded) => {
    if (!loaded) {
      return []
    }
    if (isEmptyOrNil(bals)) {
      return []
    }

    return bals
  }
)

export const selectTotalPoints = createSelector([selectLoadedBalanceHistory], (bals) => {
  if (isEmptyOrNil(bals)) {
    return 0
  }

  const keys = Object.keys(bals)
  const lastKey = keys[keys.length - 1]
  return Math.floor(bals[lastKey].runningTotalPoints)
})

export const selectLendingPoints = createSelector([selectLoadedBalanceHistory], (bals) => {
  if (isEmptyOrNil(bals)) {
    return 0
  }

  const keys = Object.keys(bals)
  const lastKey = keys[keys.length - 1]
  return Math.floor(bals[lastKey].runningTotalLendingPoints)
})

export const selectBorrowingPoints = createSelector([selectLoadedBalanceHistory], (bals) => {
  if (isEmptyOrNil(bals)) {
    return 0
  }

  const keys = Object.keys(bals)
  const lastKey = keys[keys.length - 1]
  return Math.floor(bals[lastKey].runningTotalBorrowingPoints)
})
